import 'react-app-polyfill/ie11';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import utils from '../utils';
import __ from './internetLanguage';

export default function DayRange(props) {
	const form = props.form;
	const totalResults = props.totalResults || { web: 0, journals: 0, podcasts: 0 };
	const activeTab = props.activeTab || 'web';
	const [isOpen, setIsOpen] = useState(false);
	const isMobileModal = props.id === 'mobile-dates';
	const [pendingDateRange, setPendingDateRange] = useState(null);

	const options = [
		{ key: 0, value: '0', label: __('allDates') },
		{ key: 1, value: '7', label: __('last7Days') },
		{ key: 2, value: '30', label: __('last30Days') },
		{ key: 3, value: '60', label: __('last60Days') },
		{ key: 4, value: '90', label: __('last90Days') },
	];

	// Initialize pending state when component mounts in mobile modal
	useEffect(() => {
		if (isMobileModal) {
			setPendingDateRange(form.data.dateRange || '0');
		}
	}, [isMobileModal, form.data.dateRange]);

	// Check if the active tab has results (memoized to prevent unnecessary re-calculations)
	const activeTabResults = useMemo(() => totalResults[activeTab] || 0, [totalResults, activeTab]);
	const hasActiveTabResults = activeTabResults > 0;

	// Check if date range is set to "All dates" (value '0' or empty)
	const isAllDates = useMemo(() => {
		const currentRange = isMobileModal ? pendingDateRange : form.data.dateRange;
		return !currentRange || currentRange === '0';
	}, [isMobileModal, pendingDateRange, form.data.dateRange]);

	// Hide the component if the active tab has no results and date range is set to "All dates"
	const shouldHide = !hasActiveTabResults && isAllDates;

	const toggleDropdown = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsOpen(!isOpen);
	};

	const handleDropdown = (e, option) => {
		e.preventDefault();
		e.stopPropagation();
		const value = option ? option.value : '0';

		if (isMobileModal) {
			// Mobile modal: just update pending state, don't apply immediately
			setPendingDateRange(value);
		} else {
			// Desktop: apply immediately as before
			if (value === '0') {
				form.setDate1('');
				form.setDate2('');
				form.doSearch();
			}
			form.updateDateRange(value);
		}
		setIsOpen(false);
	};

	// Expose method for TaxFilterModal to apply pending changes
	useEffect(() => {
		if (isMobileModal && props.onPendingDateRangeChange) {
			props.onPendingDateRangeChange(pendingDateRange);
		}
	}, [isMobileModal, pendingDateRange, props.onPendingDateRangeChange]);

	// Method to apply pending date range (called by TaxFilterModal's applyAll)
	const applyPendingDateRange = useCallback(() => {
		if (isMobileModal && pendingDateRange !== null) {
			const value = pendingDateRange;
			if (value === '0') {
				form.setDate1('');
				form.setDate2('');
				form.doSearch();
			}
			form.updateDateRange(value);
		}
	}, [isMobileModal, pendingDateRange, form]);

	// Expose apply method to parent component
	useEffect(() => {
		if (isMobileModal && props.onRegisterApplyMethod) {
			props.onRegisterApplyMethod(applyPendingDateRange);
		}
	}, [isMobileModal, applyPendingDateRange, props.onRegisterApplyMethod]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (isOpen && !event.target.closest('.search-widget-dropdown')) {
				setIsOpen(false);
			}
		};

		document.addEventListener('click', handleClickOutside);
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, [isOpen]);

	const getCurrentOption = () => {
		const currentRange = isMobileModal ? pendingDateRange : form.data.dateRange;
		return options.find((opt) => opt.value === currentRange) || options[0];
	};

	useEffect(() => {
		// Only run this effect if we have actual date values to check
		if (!form.data.date1 && !form.data.date2) {
			return;
		}

		options.forEach((item) => {
			const value = item.value;
			const newDate1 = utils.calcDateOffset(value);
			const newDate2 = utils.calcCurrentDate();
			if (newDate1 === form.data.date1 && newDate2 === form.data.date2) {
				// Only set if it's actually different to prevent infinite loops
				if (form.data.dateRange !== value) {
					form.setDateRange(value);
				}
			}
		});
	}, [form.data.date1, form.data.date2, form.data.dateRange]);

	const current = getCurrentOption();
	const dropdownId = `${props.id}-dropdown`;

	// Don't render if there are no results and date range is "All dates"
	if (shouldHide) {
		return null;
	}

	return (
		<div className="ml-auto">
			<div id={props.id} className="select-date-range">
				<label htmlFor={dropdownId} className="filter-date-label">
					{__('dateRange')}
				</label>
				<div className={'search-widget-dropdown' + (isOpen ? ' show' : '')} data-dropdown={isOpen}>
					<button
						className="search-widget-dropdown__toggle"
						type="button"
						id={dropdownId}
						aria-expanded={isOpen}
						onClick={toggleDropdown}
					>
						{current.label}
					</button>
					<div className="search-widget-dropdown__menu" aria-labelledby={dropdownId} role="menu">
						{options.map((option, i) => (
							<button
								key={`${option.value}${i}`}
								className={'search-widget-dropdown__item' + (current?.value === option.value ? ' active' : '')}
								onClick={e => handleDropdown(e, option)}
								role="menuitem"
							>
								{option.label}
							</button>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
